//Frontend/src/unicodex_ui/pages/ManufacturerProductsPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalApi from "../services/portalApi";
import "../styles/ManufacturerPortal.css";

function ManufacturerProductsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [products, setProducts] = useState([]);

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [packaging, setPackaging] = useState("");
  const [saving, setSaving] = useState(false);

    const [openBarcodesFor, setOpenBarcodesFor] = useState(null); // productId
    const [barcodeRows, setBarcodeRows] = useState([]);
    const [barcodeValue, setBarcodeValue] = useState("");
    const [barcodeBusyFor, setBarcodeBusyFor] = useState(null); // productId currently loading/saving barcodes
    const [barcodeErr, setBarcodeErr] = useState("");
    
    const [variantCode, setVariantCode] = useState("");

  const navigate = useNavigate();

  function signOut() {
    localStorage.removeItem("ucx_mfg_token");
    localStorage.removeItem("ucx_mfg_user");
    navigate("/portal/login", { replace: true });
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await portalApi.listProducts();
      if (!data?.ok) throw new Error("list_failed");
      setProducts(Array.isArray(data.products) ? data.products : []);
    } catch (e) {
      console.error("[MfgProducts] load error:", e);
      setError("Unable to load products.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }

    async function loadBarcodes(productId) {
    setBarcodeErr("");
    setBarcodeBusyFor(productId);
    try {
      const data = await portalApi.listProductBarcodes(productId);
      if (!data?.ok) throw new Error("barcodes_load_failed");
      setBarcodeRows(Array.isArray(data.barcodes) ? data.barcodes : []);
    } catch (e) {
      setBarcodeRows([]);
      setBarcodeErr("Unable to load barcodes.");
    } finally {
      setBarcodeBusyFor(null);
    }
  }

  async function onAddBarcode(productId) {
    const v = barcodeValue.trim();
    if (!v) return;

    setBarcodeErr("");
    setBarcodeBusyFor(null);
    try {
      const data = await portalApi.addProductBarcode(productId, {
        barcode_value: v,
        setPrimary: true,
      });
      if (!data?.ok) throw new Error(data?.error || "add_failed");
      setBarcodeValue("");
      await loadBarcodes(productId);
      await load(); // refresh products list to show primary barcode if included
    } catch (e) {
      const apiErr = e?.response?.data;
      const code = apiErr?.error;
      
      if (code === "barcode_conflict") {
        setBarcodeErr(apiErr?.message || "Barcode already exists");
        return;
      }
      
      if (code === "bad_request") {
        const m = apiErr?.message || "Invalid barcode";
        setBarcodeErr(m);
        return;
      }
      
      const msg = apiErr?.message || apiErr?.error || "Unable to add barcode.";
      setBarcodeErr(String(msg));

    } finally {
      setBarcodeBusyFor(null)
    }
  }

  async function onSetPrimary(productId, barcodeId) {
    setBarcodeErr("");
    setBarcodeBusyFor(productId)
    try {
      const data = await portalApi.setPrimaryBarcode(productId, barcodeId);
      if (!data?.ok) throw new Error(data?.error || "primary_failed");
      await loadBarcodes(productId);
      await load();
    } catch {
      setBarcodeErr("Unable to set primary barcode.");

    } finally {
      setBarcodeBusyFor(null)
    }
  }

    async function onGenerateB2(productId) {
    const raw = String(variantCode || "").trim();
    if (!raw) {
      setBarcodeErr("Enter variant code (00–99).");
      return;
    }
    const n = Number(raw.replace(/[^\d]/g, ""));
    if (Number.isNaN(n) || n < 0 || n > 99) {
      setBarcodeErr("Variant code must be between 00 and 99.");
      return;
    }

    setBarcodeErr("");
    setBarcodeBusyFor(productId);
    try {
      const data = await portalApi.generateProductBarcodeB2(productId, {
        variantCode: n,
        setPrimary: true,
      });
      if (!data?.ok) throw new Error(data?.error || "generate_failed");
      setVariantCode("");
      await loadBarcodes(productId);
      await load();
    } catch (e) {
      const apiErr = e?.response?.data;
      const code = apiErr?.error;

      if (code === "barcode_conflict") {
        setBarcodeErr(apiErr?.message || "Barcode already exists");
        return;
      }
      if (code === "bad_request") {
        setBarcodeErr(apiErr?.message || "Invalid barcode request");
        return;
      }
      setBarcodeErr(apiErr?.message || apiErr?.error || "Unable to generate barcode.");
    } finally {
      setBarcodeBusyFor(null);
    }
  }



  useEffect(() => {
    load();
  }, []);

  async function create(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const data = await portalApi.createProduct({
        name,
        category: category || null,
        packaging: packaging || null,
      });
      if (!data?.ok) throw new Error("create_failed");
      setName("");
      setCategory("");
      setPackaging("");
      await load();
    } catch (e) {
      console.error("[MfgProducts] create error:", e);
      setError("Unable to create product.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="ucx-section ucx-mfg-portal-page">
      <div className="ucx-container">

        <ManufacturerNav title="Manufacturer Portal" subtitle="Products" onSignOut={signOut} />

        <div className="ucx-mfg-panel">
          <div className="ucx-mfg-panel-head">
            <div>
              <h1 className="ucx-mfg-title">Products</h1>
              <p className="ucx-mfg-subtitle">Register products under your manufacturer prefix.</p>
            </div>
            <div className="ucx-mfg-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={load} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>

          {error && <div className="ucx-mfg-error">{error}</div>}

          <form className="ucx-mfg-form" onSubmit={create}>
            <div className="ucx-mfg-form-row">
              <div className="ucx-mfg-field">
                <label>Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required />
              </div>

              <div className="ucx-mfg-field">
                <label>Category (optional)</label>
                <input value={category} onChange={(e) => setCategory(e.target.value)} />
              </div>

              <div className="ucx-mfg-field">
                <label>Packaging (optional)</label>
                <input value={packaging} onChange={(e) => setPackaging(e.target.value)} />
              </div>

              <button type="submit" className="ucx-btn ucx-btn-primary" disabled={saving}>
                {saving ? "Saving…" : "Add"}
              </button>
            </div>
          </form>

          <div className="ucx-mfg-tablewrap">
            <table className="ucx-mfg-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th>Packaging</th>
                  <th>Barcode</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="ucx-mfg-empty">Loading…</td>
                  </tr>
                )}

                {!loading && products.length === 0 && (
                  <tr>
                    <td colSpan={7} className="ucx-mfg-empty">No products yet.</td>
                  </tr>
                )}

                {products.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr>
                      <td>{p.id}</td>
                      <td>{p.name}</td>
                      <td>{p.ucx_sku || "-"}</td>
                      <td>{p.category || "-"}</td>
                      <td>{p.packaging || "-"}</td>
                      <td>{p.barcode_value || p.barcodeValue || "-"}</td>
                      <td>
                        <button
                          type="button"
                          className="ucx-btn ucx-btn-outline"
                          onClick={async () => {
                            const pid = p.id;
                            if (openBarcodesFor === pid) {
                             setOpenBarcodesFor(null);
                             setBarcodeRows([]);
                             setBarcodeValue("");
                             setBarcodeErr("");
                             return;
                           }
                           
                           // switching to a different product: clear old state immediately
                           setBarcodeRows([]);
                           setBarcodeValue("");
                           setBarcodeErr("");
                           
                           setOpenBarcodesFor(pid);
                           await loadBarcodes(pid);
                           
                          }}
                          disabled={barcodeBusyFor === p.id}
                        >
                          {openBarcodesFor === p.id ? "Close" : "Manage"}
                        </button>
                      </td>
                    </tr>
                
                    {openBarcodesFor === p.id ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="ucx-mfg-panel" style={{ margin: "10px 0" }}>
                            <div className="ucx-mfg-panel-head" style={{ marginBottom: 8 }}>
                              <div>
                                <h3 className="ucx-mfg-title" style={{ fontSize: 16, marginBottom: 2 }}>
                                  Barcodes — Product #{p.id}
                                </h3>
                                <p className="ucx-mfg-subtitle" style={{ margin: 0 }}>
                                  Add UPC/EAN or custom barcode values and set a primary barcode.
                                </p>
                              </div>
                            </div>
                
                            {barcodeErr ? <div className="ucx-mfg-error">{barcodeErr}</div> : null}
                
                            <div className="ucx-mfg-form-row" style={{ gridTemplateColumns: "1fr auto" }}>
                              <div className="ucx-mfg-field">
                                <label>Barcode value</label>
                                <input
                                  value={barcodeValue}
                                  onChange={(e) => setBarcodeValue(e.target.value)}
                                  placeholder="Scan or type barcode"
                                  disabled={barcodeBusyFor === p.id}
                                />
                              </div>
                
                              <button
                                type="button"
                                className="ucx-btn ucx-btn-primary"
                                onClick={() => onAddBarcode(p.id)}
                                disabled={barcodeBusyFor === p.id || !barcodeValue.trim()}
                                style={{ alignSelf: "end" }}
                              >
                                {barcodeBusyFor === p.id ? "Saving…" : "Add + Set Primary"}
                              </button>
                            </div>

                                                        <div className="ucx-mfg-form-row" style={{ gridTemplateColumns: "1fr auto auto", marginTop: 10 }}>
                              <div className="ucx-mfg-field">
                                <label>Generate UNI-CODEX market barcode (Code128 B2)</label>
                                <input
                                  value={variantCode}
                                  onChange={(e) => setVariantCode(e.target.value.replace(/[^\d]/g, "").slice(0, 2))}
                                  placeholder="Variant code (00–99)"
                                  disabled={barcodeBusyFor === p.id}
                                />
                              </div>

                              <button
                                type="button"
                                className="ucx-btn ucx-btn-outline"
                                onClick={() => setVariantCode("00")}
                                disabled={barcodeBusyFor === p.id}
                                style={{ alignSelf: "end" }}
                              >
                                Use 00
                              </button>

                              <button
                                type="button"
                                className="ucx-btn ucx-btn-primary"
                                onClick={() => onGenerateB2(p.id)}
                                disabled={barcodeBusyFor === p.id}
                                style={{ alignSelf: "end" }}
                              >
                                {barcodeBusyFor === p.id ? "Generating…" : "Generate + Set Primary"}
                              </button>
                            </div>
                
                            <div className="ucx-mfg-tablewrap" style={{ marginTop: 10 }}>
                              <table className="ucx-mfg-table">
                                <thead>
                                  <tr>
                                    <th>ID</th>
                                    <th>Barcode</th>
                                    <th>Format</th>
                                    <th>Primary</th>
                                    <th>Created</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {barcodeBusyFor === p.id && !barcodeRows.length ? (
                                    <tr>
                                      <td colSpan={6} className="ucx-mfg-empty">
                                        Loading…
                                      </td>
                                    </tr>
                                  ) : barcodeRows.length ? (
                                    barcodeRows.map((b) => (
                                      <tr key={b.id}>
                                        <td>{b.id}</td>
                                        <td>{b.barcode_value}</td>
                                        <td>{b.barcode_format || "-"}</td>
                                        <td>{b.is_primary ? "yes" : "no"}</td>
                                        <td>
                                          {b.created_at ? new Date(b.created_at).toLocaleString() : "-"}
                                        </td>
                                        <td>
                                          {b.is_primary ? (
                                            <span className="ucx-mfg-subtitle">—</span>
                                          ) : (
                                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                              <button
                                                type="button"
                                                className="ucx-btn ucx-btn-outline"
                                                onClick={() => onSetPrimary(p.id, b.id)}
                                                disabled={barcodeBusyFor === p.id}
                                              >
                                                Set primary
                                              </button>
                                        
                                              <button
                                                type="button"
                                                className="ucx-btn ucx-btn-danger"
                                                onClick={async () => {
                                                  if (!window.confirm("Delete this barcode?")) return;
                                                  setBarcodeErr("");
                                                 setBarcodeBusyFor(p.id);
                                                  try {
                                                    const r = await portalApi.deleteProductBarcode(p.id, b.id);
                                                    if (!r?.ok) throw new Error(r?.error || "delete_failed");
                                                    await loadBarcodes(p.id);
                                                    await load();
                                                  } catch (e) {
                                                    const apiErr = e?.response?.data;
                                                    setBarcodeErr(apiErr?.message || apiErr?.error || "Unable to delete barcode.");
                                                  } finally {
                                                    setBarcodeBusyFor(null);
                                                  }
                                                }}
                                                disabled={barcodeBusyFor === p.id}
                                              >
                                                Delete
                                              </button>
                                            </div>
                                          )}
                                        </td>

                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={6} className="ucx-mfg-empty">
                                        No barcodes yet.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))}

              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ManufacturerProductsPage;
